import { Dec, sum } from '../money.js';
/**
 * Credit and debit notes (BLOCK-11-PLAN D-7…D-12, D-16, D-17, D-22; §7.1, §7.2). Pure rules, no I/O:
 * the API reads the rows and asks these functions; the screens show what the API answers.
 *
 * Every rule is sourced (CR = the tax compliance review, F-xx = plan §2.1) or is a temporary
 * decision of plan §4b (TD-xx), each behind its own function so an answer from Siraj's council
 * changes one function and its pinning test (DOM-N1…N9), never stored data.
 */
/** The four note kinds: domestic credit and debit, export credit and debit (ETA C, D, EC, ED). */
export const NOTE_KINDS = ['c', 'd', 'ec', 'ed'];
export const isNoteKind = (kind) => NOTE_KINDS.includes(kind);
export const isCreditKind = (kind) => kind === 'c' || kind === 'ec';
export const isDebitKind = (kind) => kind === 'd' || kind === 'ed';
/**
 * TD-10 (Q10): the invoice family a note may reference. `ec`/`ed` reference `ei` (CR:273);
 * `c`/`d` reference `i` (DERIVED). A note never references a note (CR:277; B1T:48).
 */
export function referenceFamilyOf(kind) {
    return kind === 'ec' || kind === 'ed' ? 'ei' : 'i';
}
/** TD-17 (Q18): the export rules of `ei` apply to `ec` and `ed`. */
export const isExportKind = (kind) => kind === 'ei' || kind === 'ec' || kind === 'ed';
/**
 * Issued-like (D-11): the document exists at the Tax Authority's side of the line, whatever ETA
 * said yet. `draft`, `discarded` and `superseded` are not; `issued`, `signing` and `signed` are.
 */
export function isIssuedLike(status) {
    return status !== 'draft' && status !== 'discarded' && status !== 'superseded';
}
/** ETA states after which a note no longer counts (it never took effect, or was cancelled). */
const DEAD_ETA_STATES = ['invalid', 'not_accepted', 'cancelled'];
/**
 * A live note (D-11, TD-12): issued-like and not refused or cancelled by ETA. A note in `signing`,
 * or with no ETA state yet, counts. Live notes cap the credit and block the invoice's cancel.
 */
export function isLiveNote(note) {
    return isIssuedLike(note.status) && !DEAD_ETA_STATES.includes(note.etaStatus ?? '');
}
/**
 * D-9, U-18, TD-17: the line fields copied from the referenced line and locked. The rest —
 * quantity, unit price, discount, items discount, value difference, fixed-tax amounts and, on
 * export notes, the weight quantity — are the note's own.
 */
export const NOTE_LOCKED_LINE_FIELDS = [
    'description',
    'itemType',
    'itemCode',
    'internalCode',
    'unitType',
    'taxes',
    'weightUnitType',
];
/** TD-5 (Q5, Q12): the credit cap is cumulative over live credit notes, in EGP. */
export const creditCapPolicy = { id: 'cumulative-live-credits-egp-v1' };
/** TD-6 (Q6): no cap is recorded for debit notes. Their line restriction is VERIFIED (F-14). */
export const debitCapPolicy = { id: 'no-cap-v1' };
/** TD-11 (Q11): a foreign-currency note carries its own rate for its date; the cap is in EGP. */
export const noteRatePolicy = { id: 'note-own-rate-cap-in-egp-v1' };
/**
 * D-8, D-10, TD-7, TD-16 (§7.2): the reference issues of a note. Blocking issues and warnings
 * separately; every key names the reference by its position, so the editor shows it on its row.
 * `noteIssuedAt` is the note's planned issue instant (ISO) and `noteIssueDay` its day in Cairo.
 */
export function noteReferenceIssues(input) {
    const issues = [];
    const warnings = [];
    const refs = input.references;
    if (refs.length === 0) {
        issues.push({ path: 'references', issue: 'required' });
        return { issues, warnings };
    }
    const family = referenceFamilyOf(input.kind);
    const seen = new Set();
    const noteAt = Date.parse(input.noteIssuedAt);
    refs.forEach((r, i) => {
        const path = `references.${i}`;
        if (seen.has(r.id)) {
            issues.push({ path, issue: 'duplicate' });
            return;
        }
        seen.add(r.id);
        if (!r.found) {
            issues.push({ path, issue: 'unknown_document' });
            return;
        }
        if (r.kind !== family) {
            issues.push({ path, issue: 'not_an_invoice' });
            return;
        }
        if (!r.status || !isIssuedLike(r.status)) {
            issues.push({ path, issue: 'not_issued' });
            return;
        }
        if (r.etaStatus !== 'valid')
            issues.push({ path, issue: 'not_valid_at_eta' });
        else if (r.cancelPending)
            issues.push({ path, issue: 'cancellation_pending' });
        if (r.dateTimeIssued) {
            const at = Date.parse(r.dateTimeIssued);
            // TD-16: the reference must be issued strictly before the note (timestamps).
            if (Number.isFinite(at) && Number.isFinite(noteAt) && at >= noteAt)
                issues.push({ path, issue: 'issued_after_note' });
            else if (r.issueDateLocal && r.issueDateLocal === input.noteIssueDay)
                warnings.push({ path, issue: 'same_day_as_note' });
        }
        if (r.cancelWindowOpen)
            warnings.push({ path, issue: 'cancel_window_open' });
    });
    // D-10 (F-14; U-55): every reference agrees with the first on the parties and the header.
    const usable = refs.filter((r) => r.found && r.kind === family);
    const first = usable[0];
    if (first) {
        usable.forEach((r, i) => {
            if (i === 0)
                return;
            const at = refs.indexOf(r);
            if (r.receiverType !== first.receiverType || r.receiverId !== first.receiverId)
                issues.push({ path: `references.${at}`, issue: 'other_receiver' });
        });
        const differs = (k) => usable.some((r) => (r[k] ?? null) !== (first[k] ?? null));
        if (differs('activityCode'))
            issues.push({ path: 'references', issue: 'mixed_activity_code' });
        if (differs('branchId'))
            issues.push({ path: 'references', issue: 'mixed_branch' });
        if (differs('currency'))
            issues.push({ path: 'references', issue: 'mixed_currency' });
        if (usable.length > 1 && usable.some((r) => r.receiverUnidentified))
            issues.push({ path: 'references', issue: 'single_for_unidentified_buyer' });
        // U-60: a foreign note carries its own rate (noteRatePolicy); a rate different from the
        // reference's is a warning, never a block — the cap stays in EGP (TD-11).
        if (input.noteExchangeRate && first.exchangeRate) {
            const mine = new Dec(input.noteExchangeRate);
            const theirs = new Dec(first.exchangeRate);
            if (!mine.equals(theirs))
                warnings.push({ path: 'exchangeRate', issue: 'differs_from_reference' });
        }
    }
    return { issues, warnings };
}
/**
 * D-8, D-10 (§7.2): the note's own kind must agree with the family of its references. The first
 * usable invoice reference fixes the family (sales-notes.md §2.1: "the first reference fixes …
 * the family"); a note kind of the other family is a top-level issue on `kind`, distinct from the
 * per-reference `not_an_invoice` so the editor can point at the wrong start action rather than a
 * row. Returns null when there is no usable invoice reference (then `references.N` keys speak).
 */
export function noteFamilyIssue(input) {
    const family = referenceFamilyOf(input.kind);
    const first = input.references.find((r) => r.found && (r.kind === 'i' || r.kind === 'ei'));
    if (first && first.kind !== family)
        return { path: 'kind', issue: 'note_family_mismatch' };
    return null;
}
/**
 * D-9 (F-14, F-15; TD-8): the line issues of a note. Each line points at one line of a referenced
 * document, at most once; a locked field the draft carries must equal the referenced line's.
 */
export function noteLineIssues(lines, referencedLines) {
    const issues = [];
    const seen = new Set();
    lines.forEach((line, i) => {
        const path = `lines.${i}`;
        const id = line.referencedLineId;
        if (!id) {
            issues.push({ path: `${path}.referencedLineId`, issue: 'required' });
            return;
        }
        if (seen.has(id)) {
            issues.push({ path: `${path}.referencedLineId`, issue: 'duplicate' });
            return;
        }
        seen.add(id);
        const ref = referencedLines.get(id);
        if (!ref) {
            issues.push({ path: `${path}.referencedLineId`, issue: 'not_in_references' });
            return;
        }
        for (const field of NOTE_LOCKED_LINE_FIELDS) {
            const mine = line[field];
            if (mine === undefined)
                continue; // not carried: copied from the reference
            const same = field === 'taxes'
                ? JSON.stringify(mine ?? []) === JSON.stringify(ref.taxes ?? [])
                : (mine ?? null) === (ref[field] ?? null);
            if (!same)
                issues.push({ path: `${path}.${field}`, issue: 'locked_to_reference' });
        }
    });
    return issues;
}
/**
 * D-11 (F-15, F-16; U-17; TD-5): the credit a note may still take, in EGP: the totals of its
 * references minus every other live credit note that shares any one of them (subtracted whole: an
 * overlapping prior note is not prorated, DOM-N4). Never below zero.
 */
export function creditCap(input) {
    const remaining = sum(input.referencedTotals.map((v) => new Dec(v))).minus(sum(input.otherLiveCredits.map((v) => new Dec(v))));
    return remaining.lessThan(0) ? new Dec(0) : remaining;
}
/**
 * D-11: the cap's issue, when a credit note's total exceeds it. Details for the review: the cap,
 * the other live credits, the remaining amount in EGP and, for a foreign note, at its own rate.
 */
export function creditCapIssue(input) {
    if (!isCreditKind(input.kind))
        return null; // TD-6: debit notes have no cap
    const remaining = creditCap(input);
    if (!new Dec(input.noteTotal).greaterThan(remaining))
        return null;
    const rate = input.exchangeRate ? new Dec(input.exchangeRate) : null;
    return {
        path: 'totalAmount',
        issue: 'exceeds_reference',
        params: {
            cap: sum(input.referencedTotals.map((v) => new Dec(v))).toFixed(5),
            otherCredits: sum(input.otherLiveCredits.map((v) => new Dec(v))).toFixed(5),
            remaining: remaining.toFixed(5),
            ...(rate && rate.greaterThan(0) ? { remainingForeign: remaining.div(rate).toFixed(5) } : {}),
        },
    };
}
/**
 * TD-9 (Q9; D-10): the reference a note copies its parties and header from — the first of the
 * usable references. The caller holds the reference's row shape and copies the specific fields
 * (receiver, branch, activity, currency, service-delivery date); this names the one rule it has to
 * obey, so "which reference" lives in one place (consumed by `loadNoteContext`).
 */
export function noteParties(references) {
    return references[0] ?? null;
}
//# sourceMappingURL=notes.js.map